// Repertoire browse filters — types, limits, defaults and the production-ready
// query builder. The actual network call that consumes the built query is
// stubbed in `repertoireFilterApi.ts` for now, but everything in this file is
// meant to ship as-is: `buildRepertoireFilterQuery` produces the exact param
// shape the backend filter endpoint is expected to accept.
//
// Until that endpoint exists, `filterRepertoireClientSide` applies the same
// criteria locally over the already-loaded repertoire list.

import { Genre, RepertoireData } from 'Types/RepertoireTypes';

export type RepertoireStatus = 'not_started' | 'in_progress' | 'complete' | 'perfect';

export interface StatusOption {
  id: RepertoireStatus;
  label: string;
}

export const STATUS_OPTIONS: StatusOption[] = [
  { id: 'not_started', label: 'Not Started' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'complete', label: 'Complete' },
  { id: 'perfect', label: 'Perfect' },
];

// Level dropdowns run 1..26 inclusive.
export const LEVEL_MIN = 1;
export const LEVEL_MAX = 26;

// Duration slider limits, in seconds: 0:30 .. 20:00.
export const DURATION_MIN = 30;
export const DURATION_MAX = 20 * 60;
export const DURATION_STEP = 5;

// BPM slider limits.
export const BPM_MIN = 30;
export const BPM_MAX = 300;
export const BPM_STEP = 1;

export interface GenreOption {
  /** Representative id (first seen) — used as a stable React key. */
  id: number;
  /** All genre ids that share this display name (the data repeats names across ids). */
  ids: number[];
  name: string;
}

export interface RepertoireFilters {
  statuses: RepertoireStatus[];
  minLevel: number;
  maxLevel: number;
  genreIds: number[];
  /** [min, max] in seconds */
  durationRange: [number, number];
  /** [min, max] in BPM */
  bpmRange: [number, number];
}

export const defaultFilters: RepertoireFilters = {
  statuses: [],
  minLevel: LEVEL_MIN,
  maxLevel: LEVEL_MAX,
  genreIds: [],
  durationRange: [DURATION_MIN, DURATION_MAX],
  bpmRange: [BPM_MIN, BPM_MAX],
};

/** Query params accepted by the (future) repertoire filter endpoint. */
export interface RepertoireFilterQuery {
  status?: string;
  min_level?: number;
  max_level?: number;
  genre_ids?: string;
  min_duration?: number;
  max_duration?: number;
  min_bpm?: number;
  max_bpm?: number;
}

/**
 * Convert UI filter state into backend query params. Defaults (full ranges,
 * empty multi-selects) are omitted so the request only carries meaningful
 * constraints. This is the prod-ready contract — the dummy API just reads it.
 */
export function buildRepertoireFilterQuery(filters: RepertoireFilters): RepertoireFilterQuery {
  const query: RepertoireFilterQuery = {};

  if (filters.statuses.length > 0) {
    query.status = filters.statuses.join(',');
  }

  if (filters.minLevel > LEVEL_MIN) {
    query.min_level = filters.minLevel;
  }
  if (filters.maxLevel < LEVEL_MAX) {
    query.max_level = filters.maxLevel;
  }

  if (filters.genreIds.length > 0) {
    query.genre_ids = filters.genreIds.join(',');
  }

  const [durMin, durMax] = filters.durationRange;
  if (durMin > DURATION_MIN) {
    query.min_duration = durMin;
  }
  if (durMax < DURATION_MAX) {
    query.max_duration = durMax;
  }

  const [bpmMin, bpmMax] = filters.bpmRange;
  if (bpmMin > BPM_MIN) {
    query.min_bpm = bpmMin;
  }
  if (bpmMax < BPM_MAX) {
    query.max_bpm = bpmMax;
  }

  return query;
}

/** True when any filter deviates from its default (used for the Clear control / badge). */
export function isFilterActive(filters: RepertoireFilters): boolean {
  return Object.keys(buildRepertoireFilterQuery(filters)).length > 0;
}

/** Format a duration in seconds as m:ss. */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Build the genre dropdown options from the loaded repertoire list: walk every
 * piece, collect its genres and dedupe by name (the same genre label recurs
 * under many different ids in the data), then sort alphabetically. Called once
 * the browse list loads so the genre selections reflect what's actually present.
 *
 * Each option keeps the *set* of ids that share that name so filtering can match
 * any piece tagged with any of them.
 */
export function extractGenres(pieces: RepertoireData[]): GenreOption[] {
  const byName = new Map<string, GenreOption>();
  for (const piece of pieces) {
    for (const genre of piece.genres ?? []) {
      if (!genre || genre.id == null || !genre.name) continue;
      const key = genre.name.trim().toLowerCase();
      if (!key) continue;
      const existing = byName.get(key);
      if (existing) {
        if (!existing.ids.includes(genre.id)) existing.ids.push(genre.id);
      } else {
        byName.set(key, { id: genre.id, ids: [genre.id], name: genre.name.trim() });
      }
    }
  }
  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Temporary client-side filtering, standing in for the backend filter endpoint.
 * Applies genre, level and BPM criteria:
 *  - Genre: a piece matches if it has at least one of the selected genres
 *    (no genre selected ⇒ no genre constraint).
 *  - Level: inclusive [minLevel, maxLevel] over the piece's level_number.
 *  - BPM: inclusive [min, max].
 * Status and duration are intentionally not applied yet (status is display-only;
 * duration math comes later). Pieces missing a level/bpm value are not excluded
 * by that particular criterion.
 */
export function filterRepertoireClientSide(
  pieces: RepertoireData[],
  filters: RepertoireFilters,
): RepertoireData[] {
  return pieces.filter((piece) => {
    // Level — inclusive range; skip pieces with no known level rather than drop them.
    const level = piece.level?.level_number;
    if (level != null && (level < filters.minLevel || level > filters.maxLevel)) {
      return false;
    }

    // BPM — inclusive range; skip when the piece has no bpm.
    const bpm = piece.bpm;
    if (bpm != null && bpm > 0 && (bpm < filters.bpmRange[0] || bpm > filters.bpmRange[1])) {
      return false;
    }

    // Genre — at least one selected genre present.
    if (filters.genreIds.length > 0) {
      const ids = new Set((piece.genres ?? []).map((g: Genre) => g.id));
      if (!filters.genreIds.some((id) => ids.has(id))) {
        return false;
      }
    }

    return true;
  });
}
